// front/src/features/scatter-gallery/components/ScatterGalleryScreen.tsx
import { useEffect, useState, useCallback } from 'react';
import { motion, Variants } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useRoom } from '../../../contexts/RoomContext';
import './ScatterGalleryScreen.css';
import backGroundScreen from '../../../img/background_screen.png';
import fallbackImageYoko from '../../../img/sample_image_yokonaga.png'; 
import fallbackImageTate from '../../../img/sample_image_tatenaga.png';
import apiClient from '../../../api/axiosConfig';
import { getApiBaseUrl } from '../../../shared/config/urls';

// --- 定数定義 ---
const MAIN_ANIMATION_DURATION = 3; // 秒: 各アニメーションの主要な動作時間
const STAGGER_DELAY_PER_ITEM = 0.2;  // 秒: 写真1枚あたりの遅延時間（スタッガー効果）
const DELAY_BETWEEN_STAGES_MS = MAIN_ANIMATION_DURATION * 1200; // ミリ秒: ステージ間の待機時間
const PHOTOS_PER_CYCLE = 6; // 1サイクルあたりの写真枚数

const localFallbackImages = [fallbackImageYoko, fallbackImageTate];
const FALLBACK_IMAGES = Array.from(
  { length: PHOTOS_PER_CYCLE },
  () => localFallbackImages[Math.floor(Math.random() * localFallbackImages.length)]
);


// --- ヘルパー関数 ---
const getRandomInitialVariantName = () => {
  const randomNumber = Math.floor(Math.random() * 4) + 1;
  switch (randomNumber) {
    case 1: return 'initialFromTop';
    case 2: return 'initialFromBottom';
    case 3: return 'initialFromLeft';
    case 4: return 'initialFromRight';
    default: return 'initialFromLeft';
  }
};

const getRandomExitDirectionName = () => {
  const randomNumber = Math.floor(Math.random() * 4) + 1;
  switch (randomNumber) {
    case 1: return 'exitToTop';
    case 2: return 'exitToBottom';
    case 3: return 'exitToLeft';
    case 4: return 'exitToRight';
    default: return 'exitToRight';
  }
};

const shuffleArray = (array: any[]) => {
  if (!Array.isArray(array)) {
    console.error("shuffleArray received a non-array value:", array);
    return [];
  }
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

type LocationState = {
  roomid: string;
};

type Photo = {
  src: string;
  orientation: 'landscape' | 'portrait' | 'square';
};

const ScatterGalleryScreen = ({ onAnimationEnd }: { onAnimationEnd?: () => void }) => {
  // --- State定義 ---
  const location = useLocation();
  const { roomId: contextRoomId, setRoomId } = useRoom(); // Get setRoomId from context
  const [localRoomId, setLocalRoomId] = useState<string | null>(null); // Use localRoomId for internal state

  // --- useEffect to find and set roomid ---
  useEffect(() => {
    let foundRoomId: string | null = null;
    const locationState = location.state as LocationState;

    // 1. Check location.state
    if (locationState?.roomid) {
      foundRoomId = locationState.roomid;
    } 
    // 2. Check context (if not found in state)
    else if (contextRoomId) {
      foundRoomId = contextRoomId;
    } 
    // 3. Check storage (if still not found)
    else {
      foundRoomId = sessionStorage.getItem('roomid');
    }
    
    if (foundRoomId) {
        if (foundRoomId !== localRoomId) {
            setLocalRoomId(foundRoomId);
        }
        if (foundRoomId !== contextRoomId) {
            setRoomId(foundRoomId); // Sync with context
        }
    } else {
        setLocalRoomId(null);
        setRoomId(null); // Clear context
    }

    console.log("Current roomid is:", foundRoomId);
    if (!foundRoomId) {
        console.warn("roomid is not provided. API calls might fail.");
    }
  }, [location.state, contextRoomId, setRoomId, localRoomId]); // Add localRoomId to dependencies
  
  const [photoDataOriginal, setPhotoDataOriginal] = useState<string[]>([]);
  
  const [photoPosition] = useState([
    { top: "5%", left: "10%", lotate: Math.random() * 20 - 10 }, 
    { top: "5%", left: "35%", lotate: Math.random() * 20 - 10 },
    { top: "5%", left: "60%", lotate: Math.random() * 20 - 10 }, 
    { top: "50%", left: "10%", lotate: Math.random() * 20 - 10 },
    { top: "50%", left: "35%", lotate: Math.random() * 20 - 10 }, 
    { top: "50%", left: "60%", lotate: Math.random() * 20 - 10 }
  ]);
  
  const [currentPhotoData, setCurrentPhotoData] = useState<Photo[]>([]);
  const [currentCycleInitialVariant, setCurrentCycleInitialVariant] = useState('initialFromLeft');
  const [currentCycleExitDirection, setCurrentCycleExitDirection] = useState('exitToRight');
  const [activeVariant, setActiveVariant] = useState('stage1_enterAndSettle');
  const [isLastPhotoAnimated, setIsLastPhotoAnimated] = useState(false);
  const [cycleCount, setCycleCount] = useState(0);
  const [isCycleReady, setIsCycleReady] = useState(false);

  const photoVariants: Variants = {
    initial: (custom: { entryVariantName: string }) => {
      switch (custom.entryVariantName) {
        case 'initialFromTop': return { y: "-50vh", x: 0, opacity: 0, scale: 1 };
        case 'initialFromBottom': return { y: "50vh", x: 0, opacity: 0, scale: 1 };
        case 'initialFromLeft': return { x: "-50vw", y: 0, opacity: 0, scale: 1 };
        case 'initialFromRight': return { x: "50vw", y: 0, opacity: 0, scale: 1 };
        default: return { x: "-50vw", y: 0, opacity: 0, scale: 1 };
      }
    },
    stage1_enterAndSettle: (custom: { index: number }) => ({
      x: 0, y: 0, opacity: 1, scale: 1,
      transition: { duration: MAIN_ANIMATION_DURATION, delay: custom.index * STAGGER_DELAY_PER_ITEM, ease: "easeOut" }
    }),
    stage2_exitToEdge: (custom: { index: number; exitDirectionName: string }) => {
      let targetX: string | number = 0; let targetY: string | number = 0;
      switch (custom.exitDirectionName) {
        case 'exitToTop': targetY = "-50vh"; break;
        case 'exitToBottom': targetY = "50vh"; break;
        case 'exitToLeft': targetX = "-50vw"; break;
        case 'exitToRight': default: targetX = "50vw"; break;
      }
      return {
        x: targetX, y: targetY, opacity: 0, scale: 1,
        transition: { duration: MAIN_ANIMATION_DURATION, delay: custom.index * STAGGER_DELAY_PER_ITEM, ease: "easeIn" }
      };
    }
  };

  const fetchNewImageSet = useCallback(async () => {
    console.log("Fetching a new set of images from API (staggered 0.3s)...");
    const newImageUrls: string[] = [];
    try {
      if (!localRoomId) {
        console.warn("No roomid available for API call. Skipping image fetch.");
        return [];
      }
      const apiBaseUrl = getApiBaseUrl();
      const urlNextImage = `${apiBaseUrl}/api/picture/next`;

      const fetchSingleImage = async () => {
        try {
          const response = await apiClient.get<{ ret: string, msg: string, image: string }>(`${urlNextImage}?roomId=${localRoomId}`);
          if (response.data && response.data.ret === 'true' && response.data.image) {
            return `data:image/png;base64,${response.data.image}`;
          }
        } catch (error) {
          console.error("Single image fetch failed:", error);
        }
        return null;
      };

      const fetchPromises: Promise<string | null>[] = [];
      for (let i = 0; i < PHOTOS_PER_CYCLE; i++) {
        fetchPromises.push(new Promise(resolve => {
          setTimeout(async () => {
            const result = await fetchSingleImage();
            resolve(result);
          }, i * 300); // 0.3秒(300ms)おきにリクエストを開始
        }));
      }
      
      const results = await Promise.all(fetchPromises);
      results.forEach(img => {
        if (img) newImageUrls.push(img);
      });

      console.log(`Fetched ${newImageUrls.length} new images.`);
      return newImageUrls;

    } catch (error) {
      console.error("Failed to fetch new image set:", error);
      return [];
    }
  }, [localRoomId]);

  const setupNewCycle = useCallback(async () => {
    setIsCycleReady(false); 
    console.log(`[Cycle ${cycleCount}] setupNewCycle: Fetching new images for the cycle.`);
    let newImageSet = await fetchNewImageSet();

    if (!newImageSet || newImageSet.length === 0) {
      console.warn("No images fetched. Staying empty for this cycle.");
      newImageSet = [];
    }
    
    setPhotoDataOriginal(newImageSet);
    
  }, [cycleCount, fetchNewImageSet]);

  useEffect(() => {
    if (localRoomId) {
      setupNewCycle();
    }
  }, [localRoomId, cycleCount, setupNewCycle]);

  useEffect(() => {
    if (!photoDataOriginal.length || isCycleReady) {
      return;
    }

    console.log(`[Cycle ${cycleCount}] Preloading ${photoDataOriginal.length} images and determining orientation...`);
    const preloadPromises = photoDataOriginal.map(src => {
      return new Promise<Photo>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          let orientation: Photo['orientation'] = 'square';
          if (aspectRatio > 1.1) {
            orientation = 'landscape';
          } else if (aspectRatio < 0.9) {
            orientation = 'portrait';
          }
          resolve({ src, orientation });
        };
        img.onerror = (err) => {
            console.error("Image failed to load:", src, err);
            reject(err);
        };
      });
    });

    Promise.all(preloadPromises)
      .then((loadedPhotos) => {
        console.log(`[Cycle ${cycleCount}] All images preloaded and oriented successfully.`);
        setCurrentPhotoData(shuffleArray(loadedPhotos));
        const cycleInitialVariant = getRandomInitialVariantName();
        setCurrentCycleInitialVariant(cycleInitialVariant);
        setIsLastPhotoAnimated(loadedPhotos.length === 0); // 画像がない場合は即座に完了扱いにして次のサイクルへ
        setActiveVariant('stage1_enterAndSettle');
        setIsCycleReady(true);
      })
      .catch(err => {
        console.error("Failed to preload one or more images.", err);
      });

  }, [photoDataOriginal, isCycleReady, cycleCount]);

  useEffect(() => {
    if (!isLastPhotoAnimated || !isCycleReady) return;

    if (activeVariant === 'stage1_enterAndSettle') {
      const timeoutHandle = setTimeout(() => {
        const exitDirection = getRandomExitDirectionName();
        setCurrentCycleExitDirection(exitDirection);
        setIsLastPhotoAnimated(false);
        setActiveVariant('stage2_exitToEdge');
      }, DELAY_BETWEEN_STAGES_MS);
      return () => clearTimeout(timeoutHandle);

    } else if (activeVariant === 'stage2_exitToEdge') {
      const timeoutHandle = setTimeout(() => {
        setIsCycleReady(false); 
        setIsLastPhotoAnimated(false);
        setCycleCount(prev => prev + 1);
      }, DELAY_BETWEEN_STAGES_MS);
      return () => clearTimeout(timeoutHandle);
    }
  }, [isLastPhotoAnimated, activeVariant, cycleCount, isCycleReady]);

  return (
    <div 
      className="image-stack-container"
      style={{
        backgroundImage: `url(${backGroundScreen})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        animation: 'none',
        transition: 'none'
      }}
    >
      {isCycleReady && Array.isArray(currentPhotoData) && currentPhotoData.map((photo, index) => (
        <motion.div
          key={`${cycleCount}-${photo.src}-${index}`}
          className={`figure2 image-stack-card ${photo.orientation}`}
          style={{
            top: `${photoPosition[index]?.top}`,
            left: `${photoPosition[index]?.left}`,
            rotate: `${photoPosition[index]?.lotate}deg`,
          }}
          custom={{ 
            index, 
            entryVariantName: currentCycleInitialVariant, 
            exitDirectionName: currentCycleExitDirection 
          }}
          initial="initial"
          animate={activeVariant}
          variants={photoVariants}
          onAnimationComplete={() => {
            if (index === currentPhotoData.length - 1) {
              setIsLastPhotoAnimated(true);
            }
          }}
        >
          <img src={photo.src} alt={`投稿された写真 ${index + 1}`} />
        </motion.div>
      ))}
    </div>
  );
};

export default ScatterGalleryScreen;